/**
 * Suivi d'un amendement depuis le mail d'alerte.
 *
 * Le bouton « Follaw » du mail ouvre `/amendments/<_id>?dashboard=<id>&follaw=1`. La page lit `follaw=1` une fois
 * l'utilisateur CONNECTE (aucune route sans session : un lien de mail ne peut pas faire suivre un amendement à un
 * compte qui ne l'a pas ouvert), ajoute l'amendement à `follawed_amendments`, confirme par un message, puis retire le
 * paramètre de l'adresse.
 */

type Query = Record<string, unknown>;

/** Vrai seulement pour `follaw=1` exactement (pas `true`, pas un tableau). */
export function demandeSuiviDepuisMail(query: Query): boolean {
  return query.follaw === "1";
}

/**
 * Ajoute l'identifiant à la liste suivie. N'enlève JAMAIS : un clic sur le mail ne doit pas faire cesser un suivi
 * existant (contrairement au bouton étoile de la page, qui bascule).
 *
 * Renvoie `null` quand la liste n'est pas un tableau (réponse du serveur sans `follawed_amendments`) : on n'envoie alors
 * RIEN, car écrire `[id]` écraserait la liste réelle de l'utilisateur côté serveur.
 */
export function ajouterSuivi(
  liste: unknown,
  id: string
): { liste: string[]; dejaSuivi: boolean } | null {
  if (!Array.isArray(liste)) return null;
  return liste.includes(id)
    ? { liste, dejaSuivi: true }
    : { liste: [...liste, id], dejaSuivi: false };
}

export function retirerParametreSuivi(query: Query): Query {
  const { follaw, ...reste } = query;
  void follaw;
  return reste;
}

/**
 * Page où renvoyer après la connexion. Seul un chemin interne est accepté ; tout le reste (adresse absolue, `//hote`,
 * `javascript:`, paramètre répété `?redirect=a&redirect=b` qui arrive en tableau) retombe sur `/home`.
 */
export function redirectionApresConnexion(cible: unknown): string {
  return typeof cible === "string" &&
    cible.startsWith("/") &&
    !cible.startsWith("//")
    ? cible
    : "/home";
}

const PARAMETRES_DE_CONNEXION_AUTOMATIQUE = [
  "auto_login_email",
  "auto_login_code",
];

/**
 * Chemin à recopier dans `?redirect=` : celui de la page demandée SANS les paramètres de connexion automatique, sinon le
 * code passerait dans l'historique du navigateur et un auto-login périmé serait rejoué après la connexion.
 */
export function cheminSansConnexionAutomatique(route: {
  path: string;
  query: Query;
  hash?: string;
}): string {
  const reste = new URLSearchParams();
  for (const [cle, valeur] of Object.entries(route.query)) {
    if (PARAMETRES_DE_CONNEXION_AUTOMATIQUE.includes(cle)) continue;
    for (const v of Array.isArray(valeur) ? valeur : [valeur])
      if (v !== null && v !== undefined) reste.append(cle, String(v));
  }
  const requete = reste.toString();
  return route.path + (requete ? `?${requete}` : "") + (route.hash ?? "");
}
